import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Calendar } from "lucide-react";
import { PeopleDistributionDashboard } from "../../Components/peopleDistribution/PeopleDistributionDashboard";
import {
  CLASS_KEYS,
  CLASS_LABELS,
  emptyPeopleDistributionData,
  getLocalDateString,
  hasDistributionData,
  type ClassKey,
  type PeopleDistributionData,
} from "../../services/peopleDistributionTypes";
import {
  getDistributionPersonTypes,
  getPeopleDistributionDataFromApi,
} from "../../services/peopleDistributionService";

const DEFAULT_FILTERS = {
  date: getLocalDateString(),
  selectedClasses: [] as string[],
};

function toggleClassSelection(
  value: string,
  allClassValues: string[],
  current: string[]
): string[] {
  if (value === "all") return [];

  if (current.includes(value)) {
    return current.filter((item) => item !== value);
  }

  if (current.length === 0) {
    return [value];
  }

  const next = [...current, value];
  if (allClassValues.length > 0 && next.length >= allClassValues.length) {
    return [];
  }

  return next;
}

function formatClassLabel(type: string): string {
  if (type in CLASS_LABELS) return CLASS_LABELS[type as ClassKey];
  return type
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function buildClassOptions(personTypes: string[]) {
  const base = [{ label: "All Classes", value: "all" }];
  const seen = new Set<string>(["all"]);
  const sourceTypes = personTypes.length ? personTypes : [...CLASS_KEYS];
  const dynamic = sourceTypes
    .filter((type) => {
      if (seen.has(type)) return false;
      seen.add(type);
      return true;
    })
    .map((type) => ({
      label: formatClassLabel(type),
      value: type,
    }));
  return [...base, ...dynamic];
}

export const StudentDistribution: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState(DEFAULT_FILTERS.date);
  const [selectedClasses, setSelectedClasses] = useState<string[]>(DEFAULT_FILTERS.selectedClasses);
  const [personTypes, setPersonTypes] = useState<string[]>([]);
  const [dashboardData, setDashboardData] = useState<PeopleDistributionData>(
    emptyPeopleDistributionData(DEFAULT_FILTERS.selectedClasses)
  );
  const [initialLoad, setInitialLoad] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const filters = useMemo(
    () => ({ date: selectedDate, selectedClasses }),
    [selectedDate, selectedClasses]
  );

  const classOptions = useMemo(() => buildClassOptions(personTypes), [personTypes]);
  const availableClasses = useMemo(
    () => classOptions.filter((option) => option.value !== "all").map((option) => option.value),
    [classOptions]
  );

  const loadDashboard = useCallback(async () => {
    setLoadError(null);

    try {
      const apiData = await getPeopleDistributionDataFromApi(
        filters,
        availableClasses.length ? availableClasses : CLASS_KEYS
      );
      setDashboardData(apiData);
      if (!hasDistributionData(apiData)) {
        setLoadError("No distribution records found for the selected filters.");
      }
    } catch {
      setDashboardData(emptyPeopleDistributionData(filters.selectedClasses));
      setLoadError("Failed to load distribution data. Please try again.");
    } finally {
      setInitialLoad(false);
    }
  }, [filters, availableClasses]);

  useEffect(() => {
    let cancelled = false;
    getDistributionPersonTypes().then((types) => {
      if (!cancelled) setPersonTypes(types);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  return (
    <div className="min-h-screen w-full px-3 py-4 text-slate-800 sm:px-4 sm:py-5 lg:px-6 dark:text-slate-200">
      <div className="mx-auto w-full max-w-[1600px] space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-3xl">
            People Distribution
          </h1>

          <div className="relative w-full sm:w-auto sm:min-w-[200px]">
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 sm:text-[11px]">
                Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  onClick={(e) => {
                    const el = e.currentTarget as unknown as { showPicker?: () => void };
                    el.showPicker?.();
                  }}
                  className="pointer-events-auto h-9 w-full min-w-[180px] rounded-lg border border-slate-200 bg-white py-0 pl-3 pr-10 text-sm text-slate-900 [color-scheme:light] focus:border-blue-500/40 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700/60 dark:bg-[#0b0d14] dark:text-slate-100 dark:[color-scheme:dark] [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                />
                <Calendar
                  className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500"
                  aria-hidden
                />
              </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-sm backdrop-blur-sm dark:border-slate-700/60 dark:bg-[#1C202F]/50 sm:p-4">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            {classOptions.map((option) => {
              const classValues =
                availableClasses.length > 0 ? availableClasses : [...CLASS_KEYS];
              const allExplicitlySelected =
                classValues.length > 0 &&
                classValues.every((cls) => selectedClasses.includes(cls));
              const isActive =
                option.value === "all"
                  ? selectedClasses.length === 0 || allExplicitlySelected
                  : selectedClasses.length === 0
                    ? false
                    : selectedClasses.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() =>
                    setSelectedClasses((current) =>
                      toggleClassSelection(
                        option.value,
                        availableClasses.length > 0 ? availableClasses : [...CLASS_KEYS],
                        current
                      )
                    )
                  }
                  className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "border-blue-500 bg-blue-500/15 text-blue-600 dark:border-blue-400 dark:bg-blue-500/20 dark:text-blue-300"
                      : "border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300 hover:bg-slate-100 dark:border-slate-600 dark:bg-[#161b2b] dark:text-slate-300 dark:hover:border-slate-500"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        {initialLoad ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  className="h-24 animate-pulse rounded-xl border border-slate-200 bg-slate-100 dark:border-slate-700/60 dark:bg-[#161b2b]"
                />
              ))}
            </div>
            <div className="flex min-h-[320px] items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-10 text-sm text-slate-500 dark:border-slate-700/60 dark:bg-[#161b2b] dark:text-slate-400">
              Loading people distribution…
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {loadError && (
              <p className="text-xs text-slate-500 dark:text-slate-400">{loadError}</p>
            )}
            <PeopleDistributionDashboard
              data={dashboardData}
              selectedDate={selectedDate}
              filters={filters}
              availableClasses={availableClasses.length ? availableClasses : CLASS_KEYS}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentDistribution;
